import {
  Card,
  CardContent,
  CardHeader,
  ChevronDownIcon,
  Heading,
  Text
} from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { type ReactNode, useId, useLayoutEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { CaseCardTitle } from '../stepLayout'

/** A value nothing was recorded for: absent, blank or an empty list. */
const isUnrecorded = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0)

type ReadCardProps = {
  title: ReactNode
  testId: string
  children: ReactNode
}

/** Marks an entry, e.g. a practice or a notified party: an answer in itself. */
const ENTRY_ATTRIBUTE = 'data-read-entry'

/**
 * Whether anything under the element shows an answer: an item, or an entry,
 * whose heading says what was recorded even when nothing under it was. An
 * item with nothing recorded renders nothing, so a section can only tell from
 * what is on the page; it keeps watching, as names arrive after the first paint.
 */
const useHasAnswers = <Element extends HTMLElement>() => {
  const ref = useRef<Element>(null)
  const [hasAnswers, setHasAnswers] = useState(true)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) {
      return
    }
    // A name still loading isn't an answer yet: counting it would show the
    // section, then hide it again if the name turns out to be missing.
    const check = () =>
      setHasAnswers(
        Boolean(node.querySelector(`dd:not([aria-busy]), [${ENTRY_ATTRIBUTE}]`))
      )
    check()
    const observer = new MutationObserver(check)
    observer.observe(node, {
      attributeFilter: ['aria-busy'],
      childList: true,
      subtree: true
    })

    return () => observer.disconnect()
  }, [])

  return { hasAnswers, ref }
}

/**
 * One step of the case, read only: its title, then its answers. A step with
 * no answers recorded isn't shown.
 */
export const ReadCard = ({ title, testId, children }: ReadCardProps) => {
  const { hasAnswers, ref } = useHasAnswers<HTMLDivElement>()

  return (
    <AnsweredCard
      $isHidden={!hasAnswers}
      data-testid={testId}
      padding="24"
      ref={ref}
    >
      <CardHeader>
        <CaseCardTitle weight="medium">{title}</CaseCardTitle>
      </CardHeader>
      <CardContent>
        <ReadGrid>{children}</ReadGrid>
      </CardContent>
    </AnsweredCard>
  )
}

type ReadItemProps = {
  /** The value is still being looked up, e.g. a name: holds a blank line. */
  isLoading?: boolean
  label: ReactNode
  /** Text or rendered content; an array is shown as a bulleted list. */
  value: ReactNode
  testId?: string
}

/**
 * A label over its answer. With nothing recorded it isn't shown at all, as
 * designed, so an unanswered question never reads as "No".
 */
export const ReadItem = ({
  isLoading,
  label,
  value,
  testId
}: ReadItemProps) => {
  if (!isLoading && isUnrecorded(value)) {
    return null
  }

  return (
    <Item data-testid={testId}>
      <Text as="dt" tone="secondary">
        {label}
      </Text>
      <Text as="dd" aria-busy={isLoading || undefined} weight="medium">
        {isLoading ? (
          '\u00A0'
        ) : Array.isArray(value) ? (
          <List>
            {value.map((entry, index) => (
              // Read only and never reordered, so the position is stable.
              // biome-ignore lint/suspicious/noArrayIndexKey: see above
              <li key={index}>{entry}</li>
            ))}
          </List>
        ) : (
          value
        )}
      </Text>
    </Item>
  )
}

type ReadSectionProps = {
  title: ReactNode
  testId?: string
  children: ReactNode
}

type FoldingSectionProps = ReadSectionProps & {
  /** An entry shows even with nothing under it; a group hides. */
  isEntry: boolean
}

/**
 * A titled section of answers that the reader can fold away, open to start
 * with, as designed. Its answers sit in their own two columns.
 *
 * A heading wrapping a disclosure button, not `<details>`: a `<summary>` is
 * announced as a button, and a heading inside it drops out of the headings
 * list. Folded answers stay on the page, hidden, so whether the section has
 * any is still known.
 */
const FoldingSection = ({
  isEntry,
  title,
  testId,
  children
}: FoldingSectionProps) => {
  const { hasAnswers, ref } = useHasAnswers<HTMLDivElement>()
  const [isOpen, setIsOpen] = useState(true)
  const answersId = useId()
  // An entry with nothing under it, e.g. a party ticked with no details: the
  // heading is the record, with nothing to fold.
  const isHeadingOnly = isEntry && !hasAnswers

  return (
    <Group
      $isHidden={!isEntry && !hasAnswers}
      $isOpen={isOpen && !isHeadingOnly}
      data-testid={testId}
      ref={ref}
      {...(isEntry && { [ENTRY_ATTRIBUTE]: '' })}
    >
      <Heading as="h3" size="sm" weight="medium">
        {isHeadingOnly ? (
          title
        ) : (
          <Toggle
            aria-controls={answersId}
            aria-expanded={isOpen}
            onClick={() => setIsOpen((wasOpen) => !wasOpen)}
            type="button"
          >
            {title}
            <Chevron $isOpen={isOpen} aria-hidden />
          </Toggle>
        )}
      </Heading>
      <ReadGrid hidden={!isOpen || isHeadingOnly} id={answersId}>
        {children}
      </ReadGrid>
    </Group>
  )
}

/**
 * A group of a card's own answers, e.g. Incident information's log details or
 * Duty of Candour's communication details; with none recorded it isn't shown.
 */
export const ReadGroup = (props: ReadSectionProps) => (
  <FoldingSection isEntry={false} {...props} />
)

/**
 * One repeated entry of a card, e.g. a practice, an action, a debrief or a
 * notified party. It is recorded in itself, so it shows even with nothing
 * recorded under its heading.
 */
export const ReadEntry = (props: ReadSectionProps) => (
  <FoldingSection isEntry {...props} />
)

const { color, spacing } = cssVars

/** With nothing recorded under it, a section shows nothing, not a bare heading. */
const hiddenWithoutAnswers = css<{ $isHidden: boolean }>`
  ${({ $isHidden }) => $isHidden && 'display: none;'}
`

const AnsweredCard = styled(Card)<{ $isHidden: boolean }>`
  ${hiddenWithoutAnswers}
`

/**
 * Two columns of answers, one on small screens. Each answer is its own `<dl>`,
 * so groups and repeated entries can sit among them as plain blocks.
 */
const ReadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing[200]} ${spacing[300]};

  &[hidden] {
    display: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const Item = styled.dl`
  display: flex;
  flex-direction: column;
  gap: ${spacing[50]};
  margin: 0;

  /* Free text keeps the line breaks it was written with. */
  dd {
    margin: 0;
    white-space: pre-wrap;
  }
`

/** A bulleted list, 8px apart. */
export const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing[100]};
  margin: 0;
  padding-left: ${spacing[300]};
`

/**
 * Across the whole card, out through its side padding, with a rule above
 * unless it opens the card.
 */
const sectionRule = css`
  grid-column: 1 / -1;
  /* ReadCard's side padding: the card's padding="24" is spacing 300. */
  margin: 0 calc(-1 * ${spacing[300]});
  padding: 0 ${spacing[300]};

  &:not(:first-child) {
    border-top: 1px solid ${color.border.subtle};
    padding-top: ${spacing[200]};
  }
`

const Group = styled.div<{ $isHidden: boolean; $isOpen: boolean }>`
  ${sectionRule}
  display: flex;
  flex-direction: column;
  gap: ${({ $isOpen }) => ($isOpen ? spacing[200] : 0)};
  overflow-wrap: anywhere;
  ${hiddenWithoutAnswers}
`

/** The heading's whole row toggles the section; it looks like the heading. */
const Toggle = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  justify-content: space-between;
  padding: 0;
  text-align: start;
  width: 100%;
`

/** Down while folded, up while open, as designed. */
const Chevron = styled(ChevronDownIcon)<{ $isOpen: boolean }>`
  color: ${color.icon.action.secondary.default};
  flex-shrink: 0;
  ${({ $isOpen }) => $isOpen && 'transform: rotate(180deg);'}
`

/** An item that takes the full width of the two columns, e.g. long text. */
export const ReadWide = styled.div`
  grid-column: 1 / -1;

  /* Its item had nothing recorded, so it takes no row. */
  &:empty {
    display: none;
  }
`
